import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import Image from 'next/image'
import {
  CFooterLinks,
  CLogo,
  HeaderTopNavMenuType,
  HeaderTopNavSubmenuType,
} from 'tsconfig/craft-types'
import { MouseEventHandler, useState } from 'react'
import TagManager from 'react-gtm-module'
import { useSiteInfo, isValidHref } from 'shared'
import { ASvg } from 'ui/components/atoms/a-svg'
import SvgVisianIclLogo from 'ui/svg/logos/visian-icl/teal-black.svg'
import SvgStaarSurgicalLogo from 'ui/svg/logos/staar-surgical/teal-black.svg'
import SvgEvoVisianIclLogo from 'ui/svg/logos/evo-visian-icl/evo-icl-linear-black.svg'
import SvgCloseIcon from 'ui/svg/icons/stroke/close.svg'
import SvgArrowLeftIcon from 'ui/svg/icons/stroke/arrow-left.svg'
import SvgArrowRightIcon from 'ui/svg/icons/stroke/arrow-right.svg'
import SvgFacebookIcon from 'ui/svg/icons/social/Facebook.svg'
import SvgInstagramIcon from 'ui/svg/icons/social/Instagram.svg'
import SvgYoutubeIcon from 'ui/svg/icons/social/Youtube.svg'
import SvgVimeoIcon from 'ui/svg/icons/social/Vimeo.svg'
import './c-the-menu.css'
import * as CONST from 'ui/constants/index'

// Safety Information types (only used by doctors app)
type CTopBarItem = {
  showSafetyInformationLink?: boolean
  safetyInformationLinkText?: string
  safetyInformationLinkUrl?: string
}

type Props = {
  isOpen?: boolean
  brand?: string
  iclBaseUrl?: string
  doctorsUrl?: string
  quizUrl?: string
  discoveryUrl?: string
  menuRef?: any
  onClose?: Function
  onOpenLocaleSwitcher?: Function
  fullDomain?: string
  headerTopNavMenu?: HeaderTopNavMenuType[]
  topFooterLinks?: Array<CFooterLinks>
  bottomFooterLinks?: Array<CFooterLinks>
  logo?: Array<CLogo>
  headerTopBar?: Array<CTopBarItem>
}

const defaultProps = {
  isOpen: false,
  brand: '',
  iclBaseUrl: '',
  doctorsUrl: '',
  quizUrl: '',
  fullDomain: '',
}

type CustomLinkProps = {
  className?: string
  href?: string
  onClick?: MouseEventHandler
  children?: any
  target?: string
}

const CustomLink = ({
  className,
  href,
  onClick,
  children,
  target,
}: CustomLinkProps) => {
  if (!isValidHref(href)) {
    return <></>
  }

  if (/^http/.test(href ?? '')) {
    return (
      <a className={className} href={href} onClick={onClick} target={target}>
        {children}
      </a>
    )
  } else {
    return (
      <Link
        className={className}
        href={href ?? '#'}
        onClick={onClick}
        prefetch={false}
        target={target}
      >
        {children}
      </Link>
    )
  }
}

const CTheMenu = (props: Props) => {
  const { t } = useTranslation('common')
  const siteInfo = useSiteInfo()

  const shouldHideAuNavItems =
    siteInfo.iso === 'en-AU' ||
    ['southEastAsia', 'australia', 'newZealand'].includes(siteInfo.handle ?? '')

  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null)

  const [activeCustomSubmenu, setActiveCustomSubmenu] = useState<
    HeaderTopNavSubmenuType[] | null
  >(null)

  const closeMenu = () => {
    if (props.onClose instanceof Function) {
      props.onClose()
    }
    setTimeout(() => {
      setActiveSubmenu(null)
    }, 400)
  }

  const openSubmenu = (menu: string) => {
    setActiveSubmenu(menu)
  }

  const openCustomSubmenu = (
    submenu: HeaderTopNavSubmenuType[] | undefined
  ) => {
    if (submenu) {
      setActiveCustomSubmenu(submenu)
    }
  }

  const closeCustomSubmenu = () => {
    setActiveCustomSubmenu(null)
  }

  const closeSubmenu = () => {
    setActiveSubmenu(null)
  }

  const handleQuizButtonClick = () => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'quizIntention',
        event_category: 'Quiz Intention',
        event_action: 'Header component',
        event_label: window.location.href,
      },
    })
    closeMenu()
  }

  const handleDoctorsButtonClick = () => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'searchIntention',
        event_category: 'Doctor or Location Intention',
        event_action: 'Header component',
        event_label: window.location.href,
      },
    })
    closeMenu()
  }

  const handleLocaleButtonClick = () => {
    if (props.onOpenLocaleSwitcher instanceof Function) {
      props.onOpenLocaleSwitcher()
    }
    closeMenu()
  }

  return (
    <nav
      ref={props.menuRef}
      className={classNames(
        'fixed top-0 z-50 flex flex-col w-full h-full overflow-auto transition-all duration-500 inset-inline-0 c-the-menu bg-neutral-dark-gray text-neutral-white',
        {
          'invisible opacity-0': !props.isOpen,
        }
      )}
      aria-label={`${t('menu')} ${t('navigation')}`}
    >
      <div className="c-the-menu__main sticky top-0 z-50 flex items-center justify-between flex-shrink-0 w-full px-5 bg-neutral-white">
        {siteInfo.isES ? (
          <CustomLink
            className="inline-block font-bold tracking-wider uppercase text-neutral-dark-gray hover:text-primary-teal transition duration-300"
            href={`${props.fullDomain}/`}
            onClick={closeMenu}
          >
            Home
          </CustomLink>
        ) : props.logo?.length ? (
          <CustomLink
            className="inline-block relative w-[160px] h-[32px]"
            href={`${props.fullDomain}/`}
            onClick={closeMenu}
          >
            <Image
              src={props.logo?.[0].url || ''}
              priority
              alt={'EVO Visian logo'}
              fill
              style={{
                objectFit: 'cover',
              }}
            />
          </CustomLink>
        ) : (
          <CustomLink
            className="inline-block"
            href={`${props.fullDomain}/`}
            onClick={closeMenu}
          >
            {props.brand === CONST.BRANDED.VISIAN_ICL && (
              <ASvg
                className="fill-[#57606c] c-the-menu__logo-graphic"
                svg={SvgVisianIclLogo}
                aria-label={`Visian ICL ${t('logo')}`}
              />
            )}
            {props.brand === CONST.BRANDED.STAAR_SURGICAL && (
              <ASvg
                className="fill-[#57606c] c-the-menu__logo-graphic"
                svg={SvgStaarSurgicalLogo}
                aria-label={`Staar Surgical ${t('logo')}`}
              />
            )}
            {props.brand === CONST.BRANDED.EVO_VISIAN_ICL && (
              <ASvg
                className="fill-[#57606c] c-the-menu__logo-graphic"
                svg={SvgEvoVisianIclLogo}
                aria-label={`EVO Visian ${t('logo')}`}
              />
            )}
          </CustomLink>
        )}
        <button
          className="rounded-full mis-8 border-2 p-[6px] border-neutral-white text-neutral-dark-gray  hover:bg-neutral-white hover:text-neutral-dark-gray"
          onClick={closeMenu}
        >
          <ASvg
            className="stroke-current w-6 h-6"
            svg={SvgCloseIcon}
            aria-label={t('close')}
          />
        </button>
      </div>
      <div className="c-the-menu__content flex flex-col flex-grow">
        <ul className="relative mt-0 overflow-hidden c-the-menu__list">
          {!shouldHideAuNavItems && (
            <ul
              className={classNames('c-the-menu__sub-list', {
                'c-the-menu__sub-list--active':
                  (activeCustomSubmenu && activeCustomSubmenu?.length > 0) ||
                  activeSubmenu === 'benefits',
              })}
            >
              {props?.headerTopNavMenu && props.headerTopNavMenu.length > 0 ? (
                <>
                  <li className="c-the-menu__item border-t border-neutral-primary-gray">
                    <button
                      className="flex items-center justify-center w-full p-6 font-semibold uppercase text-button text-neutral-light-gray"
                      onClick={closeCustomSubmenu}
                    >
                      <ASvg
                        className="h-6 text-white stroke-current c-the-menu__arrow-icon"
                        svg={SvgArrowLeftIcon}
                      />
                      <span>{t('back')}</span>
                    </button>
                  </li>
                  {activeCustomSubmenu?.map((subItem, i) => (
                    <li
                      className="border-t border-neutral-primary-gray c-the-menu__item"
                      key={i}
                      style={{
                        order: subItem.linkOrder,
                      }}
                    >
                      <CustomLink
                        className="block capitalize text-center p-4 font-semibold transition-colors duration-300 text-p hover:text-neutral-black"
                        href={subItem.linkUrl}
                        target={subItem.openNewTab ? '_blank' : undefined}
                        onClick={() => {
                          closeCustomSubmenu()
                          closeMenu()
                        }}
                      >
                        <span>{subItem.linkTitle}</span>
                      </CustomLink>
                    </li>
                  ))}
                </>
              ) : (
                <>
                  <li className="c-the-menu__item border-t border-neutral-primary-gray">
                    <button
                      className="flex items-center justify-center w-full p-6 font-semibold uppercase text-button text-neutral-light-gray"
                      onClick={closeSubmenu}
                    >
                      <ASvg
                        className="h-6 text-white stroke-current c-the-menu__arrow-icon"
                        svg={SvgArrowLeftIcon}
                      />
                      <span>{t('back')}</span>
                    </button>
                  </li>
                  {t('paths.nightvision') && !siteInfo.isIN && (
                    <li className="border-t border-neutral-primary-gray c-the-menu__item">
                      <CustomLink
                        className="block p-6 font-semibold text-center uppercase text-button text-neutral-light-gray"
                        href={`${props.discoveryUrl}${t('paths.nightvision')}`}
                        onClick={closeMenu}
                      >
                        <span>{t('titles.night-vision')}</span>
                      </CustomLink>
                    </li>
                  )}
                  {t('paths.dry-eye') && (
                    <li className="border-t border-neutral-primary-gray c-the-menu__item">
                      <CustomLink
                        className="block p-6 font-semibold text-center uppercase text-button text-neutral-light-gray"
                        href={`${props.discoveryUrl}${t('paths.dry-eye')}`}
                        onClick={closeMenu}
                      >
                        <span>{t('titles.no-dry-eye')}</span>
                      </CustomLink>
                    </li>
                  )}
                  {t('paths.harmony') && (
                    <li className="border-t border-b border-neutral-primary-gray c-the-menu__item">
                      <CustomLink
                        className="block p-6 font-semibold text-center uppercase text-button text-neutral-light-gray"
                        href={`${props.discoveryUrl}${t('paths.harmony')}`}
                        onClick={closeMenu}
                      >
                        <span>{t('titles.harmony-w-natural-eye')}</span>
                      </CustomLink>
                    </li>
                  )}
                </>
              )}
            </ul>
          )}
          {props?.headerTopNavMenu && props.headerTopNavMenu.length > 0 ? (
            <>
              {props.quizUrl &&
                !siteInfo.isES &&
                !siteInfo.isUS &&
                !siteInfo.isCA &&
                !siteInfo.isGE &&
                !siteInfo.isUK &&
                !shouldHideAuNavItems && (
                  <li className="border-t border-neutral-primary-gray c-the-menu__item">
                    <CustomLink
                      className="block p-6 font-semibold text-center uppercase bg-neutral-dark-gray text-button text-neutral-white"
                      href={props.quizUrl}
                      target={props.quizUrl ? '_blank' : '_self'}
                      onClick={handleQuizButtonClick}
                    >
                      <span>{t('titles.take-our-quiz')}</span>
                    </CustomLink>
                  </li>
                )}
              {props.doctorsUrl && !siteInfo.isSE && (
                <li className="c-the-menu__item">
                  <CustomLink
                    className="block p-6 font-bold text-center uppercase text-button text-neutral-white bg-primary-teal"
                    href={props.doctorsUrl ?? ''}
                    target={props.doctorsUrl !== '/' ? '_blank' : '_self'}
                    onClick={handleDoctorsButtonClick}
                  >
                    <span>{t('titles.find-a-doctor')}</span>
                  </CustomLink>
                </li>
              )}
              {!shouldHideAuNavItems &&
                props.headerTopNavMenu.map((topMenu, i) => (
                  <li
                    className="border-t border-neutral-primary-gray c-the-menu__item"
                    key={i}
                    style={{
                      order: topMenu.linkOrder,
                    }}
                  >
                    <CustomLink
                      className="flex items-center justify-center w-full p-6 font-semibold uppercase text-button text-neutral-light-gray"
                      href={topMenu.linkUrl}
                      target={topMenu.openNewTab ? '_blank' : '_self'}
                      onClick={() => {
                        if (topMenu.submenu && topMenu.submenu.length > 0) {
                          openCustomSubmenu(topMenu?.submenu)
                        }
                      }}
                    >
                      <span> {topMenu.linkTitle} </span>
                      <ASvg
                        className={`h-6 text-white stroke-current c-the-menu__arrow-icon ${
                          topMenu.submenu?.length === 0 && '!hidden'
                        }`}
                        svg={SvgArrowRightIcon}
                        ariaHidden="true"
                      />
                    </CustomLink>
                  </li>
                ))}
            </>
          ) : (
            <>
              {props.quizUrl &&
                !siteInfo.isES &&
                !siteInfo.isUS &&
                !siteInfo.isCA &&
                !siteInfo.isGE &&
                !siteInfo.isUK &&
                !shouldHideAuNavItems && (
                  <li className="border-t border-neutral-primary-gray c-the-menu__item">
                    <CustomLink
                      className="block p-6 font-semibold text-center uppercase bg-neutral-dark-gray text-button text-neutral-white"
                      href={props.quizUrl}
                      target={props.quizUrl ? '_blank' : '_self'}
                      onClick={handleQuizButtonClick}
                    >
                      <span>{t('titles.take-our-quiz')}</span>
                    </CustomLink>
                  </li>
                )}
              {props.doctorsUrl && (
                <li className="c-the-menu__item">
                  <CustomLink
                    className="block p-6 font-bold text-center uppercase text-button text-neutral-white bg-primary-teal"
                    href={props.doctorsUrl ?? ''}
                    target={props.doctorsUrl !== '/' ? '_blank' : '_self'}
                    onClick={handleDoctorsButtonClick}
                  >
                    <span>{t('titles.find-a-doctor')}</span>
                  </CustomLink>
                </li>
              )}
              {!siteInfo.isES && !shouldHideAuNavItems && (
                <>
                  {t('paths.lens') && (
                    <li className="border-t border-neutral-primary-gray c-the-menu__item">
                      <CustomLink
                        className="block p-6 font-semibold text-center uppercase text-button text-neutral-light-gray"
                        href={`${props.discoveryUrl}${t('paths.lens')}`}
                        onClick={closeMenu}
                      >
                        <span>{t('titles.the-lens')}</span>
                      </CustomLink>
                    </li>
                  )}
                  {(t('paths.nightvision') ||
                    t('paths.dry-eye') ||
                    t('paths.harmony')) && (
                    <li className="border-t border-neutral-primary-gray c-the-menu__item">
                      <button
                        className="flex items-center justify-center w-full p-6 font-semibold text-center uppercase text-button text-neutral-light-gray"
                        onClick={() => openSubmenu('benefits')}
                      >
                        <span>{t('benefits')}</span>
                        <ASvg
                          className="h-6 text-white stroke-current c-the-menu__arrow-icon"
                          svg={SvgArrowRightIcon}
                        />
                      </button>
                    </li>
                  )}
                  {t('paths.procedure') && (
                    <li className="border-t border-neutral-primary-gray c-the-menu__item">
                      <CustomLink
                        className="block p-6 font-semibold text-center uppercase text-button text-neutral-light-gray"
                        href={`${props.discoveryUrl}${t('paths.procedure')}`}
                        onClick={closeMenu}
                      >
                        <span>{t('titles.procedure')}</span>
                      </CustomLink>
                    </li>
                  )}
                  {t('paths.about-visian') && (
                    <li className="border-t border-neutral-primary-gray c-the-menu__item">
                      <CustomLink
                        className="block p-6 font-semibold text-center uppercase text-button text-neutral-light-gray"
                        href={`${props.discoveryUrl}${t('paths.about-visian')}`}
                        onClick={closeMenu}
                      >
                        <span>{t('titles.about-visian')}</span>
                      </CustomLink>
                    </li>
                  )}
                  {t('paths.why-visian') && (
                    <li className="border-t border-neutral-primary-gray c-the-menu__item">
                      <CustomLink
                        className="block p-6 font-semibold text-center uppercase text-button text-neutral-light-gray"
                        href={`${props.discoveryUrl}${t('paths.why-visian')}`}
                        onClick={closeMenu}
                      >
                        <span>{t('titles.why-visian')}</span>
                      </CustomLink>
                    </li>
                  )}
                  {t('paths.life-with-visian') &&
                    !siteInfo.isIN &&
                    !siteInfo.isJP && (
                      <li className="border-t border-neutral-primary-gray c-the-menu__item">
                        <CustomLink
                          className="block p-6 font-semibold text-center uppercase text-button text-neutral-light-gray"
                          href={`${props.discoveryUrl}${t(
                            'paths.life-with-visian'
                          )}`}
                          onClick={closeMenu}
                        >
                          <span>{t('titles.life-with-visian')}</span>
                        </CustomLink>
                      </li>
                    )}
                  {t('paths.faq') && !siteInfo.isIN && (
                    <li className="border-t border-neutral-primary-gray c-the-menu__item">
                      <CustomLink
                        className="block p-6 font-semibold text-center uppercase text-button text-neutral-light-gray"
                        href={`${props.discoveryUrl}${t('paths.faq')}`}
                        onClick={closeMenu}
                      >
                        <span>{t('titles.faq')}</span>
                      </CustomLink>
                    </li>
                  )}
                </>
              )}
              {t('paths.blog') && !shouldHideAuNavItems && (
                <li className="border-t border-neutral-primary-gray c-the-menu__item">
                  <CustomLink
                    className="block p-6 font-semibold text-center uppercase text-button text-neutral-light-gray"
                    href={`${props.discoveryUrl}${t('paths.blog')}`}
                    onClick={closeMenu}
                  >
                    <span>{t('titles.visian-icl-blog')}</span>
                  </CustomLink>
                </li>
              )}
            </>
          )}
        </ul>
        {props.bottomFooterLinks && props.bottomFooterLinks?.length > 0 ? (
          !shouldHideAuNavItems && (
            <ul className="c-the-menu__list c-the-menu__list--secondary">
              {props.bottomFooterLinks.map((link: CFooterLinks) => (
                <li
                  className="c-the-menu__item"
                  key={link.linkUrl || link.linkName}
                >
                  <CustomLink
                    className="block p-4 font-bold text-center uppercase text-button text-neutral-white"
                    href={`${link.linkUrl}`}
                    onClick={closeMenu}
                  >
                    <span>{link.linkName}</span>
                  </CustomLink>
                </li>
              ))}
            </ul>
          )
        ) : (
          !shouldHideAuNavItems && (
            <ul className="c-the-menu__list c-the-menu__list--secondary">
              {t('paths.contact') && !siteInfo.isES && (
                <li className="c-the-menu__item">
                  <CustomLink
                    className="block p-4 font-bold text-center uppercase text-button text-neutral-white"
                    href={`${props.discoveryUrl}${t('paths.contact')}`}
                    onClick={closeMenu}
                  >
                    <span>{t('titles.contact')}</span>
                  </CustomLink>
                </li>
              )}
              {(() => {
                // Support headerTopBar as array or single object
                const safetyInfo = Array.isArray(props.headerTopBar)
                  ? props.headerTopBar[0]
                  : props.headerTopBar
                // Hide safety info for Brazilian Portuguese site
                if (siteInfo?.iso === 'pt-BR') return null
                if (safetyInfo?.showSafetyInformationLink === false) return null
                const linkText =
                  safetyInfo?.safetyInformationLinkText?.trim() ||
                  t('titles.safety-information')
                // Only build default URL if discoveryUrl and translated path exist
                const pathSegment = t('paths.safety-information')
                const defaultUrl =
                  props.discoveryUrl && pathSegment
                    ? `${props.discoveryUrl}${pathSegment}`
                    : undefined
                const linkUrl =
                  safetyInfo?.safetyInformationLinkUrl?.trim() || defaultUrl
                const shouldRender = Boolean(linkText && linkUrl)
                return shouldRender ? (
                  <li className="c-the-menu__item">
                    <CustomLink
                      className="block p-4 font-bold text-center uppercase text-button text-neutral-white"
                      href={linkUrl}
                      onClick={closeMenu}
                    >
                      <span>{linkText}</span>
                    </CustomLink>
                  </li>
                ) : null
              })()}
              {!siteInfo.isES && (
                <li className="c-the-menu__item">
                  <button
                    className="block p-4 font-bold text-center uppercase cursor-pointer text-button text-neutral-white w-full"
                    onClick={handleLocaleButtonClick}
                  >
                    <span>{t('international-sites')}</span>
                  </button>
                </li>
              )}
            </ul>
          )
        )}
        {!siteInfo.isES && !shouldHideAuNavItems && (
          <ul className="flex items-center justify-center px-4 pt-4 pb-8 space-i-12 c-menu__socials-list">
            {t('urls.facebook') && (
              <li>
                <a
                  className="block text-primary-gray"
                  href={t('urls.facebook') ?? ''}
                  target="_blank"
                  rel="noreferrer"
                >
                  <ASvg
                    className="w-6 h-6 fill-current"
                    svg={SvgFacebookIcon}
                    aria-label={`Facebook ${t('icon')}`}
                  />
                </a>
              </li>
            )}
            {t('urls.instagram') && (
              <li>
                <a
                  className="block text-primary-gray"
                  href={t('urls.instagram') ?? ''}
                  target="_blank"
                  onClick={closeMenu}
                  rel="noreferrer"
                >
                  <ASvg
                    className="w-6 h-6 fill-current"
                    svg={SvgInstagramIcon}
                    aria-label={`Instagram ${t('icon')}`}
                  />
                </a>
              </li>
            )}
            {t('urls.youtube') && (
              <li>
                <a
                  className="block text-primary-gray"
                  href={t('urls.youtube') ?? ''}
                  target="_blank"
                  onClick={closeMenu}
                  rel="noreferrer"
                >
                  <ASvg
                    className="w-6 h-6 fill-current"
                    svg={SvgYoutubeIcon}
                    aria-label={`Youtube ${t('icon')}`}
                  />
                </a>
              </li>
            )}
            {t('urls.vimeo') && (
              <li>
                <a
                  className="block text-primary-gray"
                  href={t('urls.vimeo') ?? ''}
                  target="_blank"
                  onClick={closeMenu}
                  rel="noreferrer"
                >
                  <ASvg
                    className="w-6 h-6 fill-current"
                    svg={SvgVimeoIcon}
                    aria-label={`Vimeo ${t('icon')}`}
                  />
                </a>
              </li>
            )}
          </ul>
        )}
      </div>
    </nav>
  )
}

CTheMenu.defaultProps = defaultProps

export { CTheMenu }
